import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { 
  Search, Calendar, Table2, AlertTriangle, CheckCircle, Clock, 
  Syringe, Pill, Activity, Edit2, Save, X,
  ChevronLeft, ChevronRight, Filter, Download, RefreshCw, Stethoscope,
  Share2, Mail, Printer, FileDown, Loader2, Eye, MessageSquare
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Horseshoe icon component for farrier
const HorseshoeIcon = ({ className = "w-4 h-4" }) => (
  <svg 
    viewBox="-21.61 -21.61 259.36 259.36" 
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="12"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ transform: 'rotate(-45deg) scaleY(-1)' }}
  >
    <path d="M108.082,8.414C48.48,8.414,0,63.952,0,132.222c0,26.905,7.451,52.546,21.56,74.133c0.919,1.383,2.796,1.785,4.188,0.88 l30.396-19.854c1.398-0.91,1.791-2.789,0.883-4.185c-9.593-14.694-14.67-32.313-14.67-50.962c0-44.904,29.483-81.439,65.713-81.439 c36.229,0,65.713,36.535,65.713,81.439c0,18.648-5.078,36.268-14.671,50.962c-0.904,1.396-0.521,3.274,0.881,4.185l30.399,19.854 c0.514,0.337,1.087,0.491,1.648,0.491c0.993,0,1.951-0.484,2.542-1.371c14.103-21.587,21.557-47.228,21.557-74.133 C216.163,63.952,167.682,8.414,108.082,8.414z M191.166,200.49l-25.345-16.544c9.191-15.144,14.026-32.952,14.026-51.713 c0-48.253-32.196-87.491-71.766-87.491c-39.573,0-71.766,39.238-71.766,87.485c0,18.76,4.847,36.569,14.029,51.713l-25.349,16.55 c-12.413-20.049-18.956-43.586-18.956-68.263c0-64.933,45.776-117.755,102.029-117.755c56.249,0,102.028,52.828,102.028,117.755 C210.11,156.893,203.567,180.436,191.166,200.49z M178.304,62.602c3.96,5.399,4.918,11.659,2.128,13.964 c-2.79,2.317-8.275-0.187-12.235-5.592c-3.954-5.405-4.906-11.659-2.116-13.964C168.859,54.698,174.326,57.205,178.304,62.602z M198.874,114.134c0.319,6.839-2.306,12.531-5.84,12.709c-3.534,0.183-6.667-5.208-6.963-12.059 c-0.319-6.832,2.3-12.519,5.834-12.714C195.445,101.89,198.554,107.29,198.874,114.134z M50.097,57.01 c2.79,2.305,1.847,8.559-2.119,13.964c-3.963,5.405-9.454,7.909-12.232,5.592c-2.79-2.305-1.844-8.564,2.125-13.964 C41.836,57.205,47.307,54.698,50.097,57.01z M30.095,114.772c-0.311,6.851-3.428,12.241-6.969,12.07 c-3.535-0.178-6.156-5.876-5.834-12.715c0.313-6.844,3.425-12.238,6.966-12.063C27.792,102.254,30.408,107.939,30.095,114.772z M116.277,31.608c0,5.222-3.979,9.454-8.905,9.454c-4.915,0-8.901-4.232-8.901-9.454c0-5.231,3.975-9.455,8.901-9.455 C112.287,22.154,116.277,26.383,116.277,31.608z"/>
  </svg>
);

// Tooth icon component for dentist
const ToothIcon = ({ className = "w-4 h-4" }) => (
  <svg 
    viewBox="0 0 512 512" 
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="20"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M100.9,374.4c2.2-0.2,3.8-2.2,3.5-4.4c-0.7-6.4-1.3-13-1.8-19.8c-0.2-2.2-2.1-3.9-4.3-3.7c-2.2,0.2-3.8,2.1-3.7,4.3c0.5,6.9,1.2,13.6,1.9,20c0.2,2,2,3.6,4,3.6C100.6,374.4,100.8,374.4,100.9,374.4z"/>
    <path d="M442.2,53.4c-17.5-20.5-42.6-31.2-72.8-31.2c-15.2,0-36.9,6.9-57.9,13.6C291.1,42.2,270,49,256,49c0,0,0,0,0,0c-13.9,0-35.1-6.8-55.5-13.3C179.5,29,157.8,22,142.6,22c-28.7,0-53.2,10.3-70.9,29.7C54.2,71,44.9,97.5,44.9,128.4c0,19.9,12.4,47.9,24.4,74.9c11.5,25.9,23.3,52.6,23,69.8c-0.2,8.4-0.2,21.9,0.3,39c0,0,0,0,0,0c0.2,6.2,0.4,12.5,0.8,18.6c0.1,2.1,1.9,3.8,4,3.8c0.1,0,0.1,0,0.2,0c2.2-0.1,4.1-2,4-4.2c-0.3-6.1-0.5-12.3-0.5-18.5c0,0,0,0,0,0c-1-16.9-0.6-30.3-0.5-38.6c0.4-18.9-11.4-45.3-23.8-73.2C65.1,173.8,53,146.7,53,128.5c0-58.9,36-98.4,89.7-98.4c13.9,0,35.1,6.8,55.5,13.2C219.1,50,240.8,57,256,57c0,0,0,0,0,0c15.2,0,36.9-7,57.9-13.6c20.4-6.4,41.5-13.3,55.5-13.3c54.5,0,89.7,38.6,89.7,98.3c0,19.4-12.6,49-24.9,77.7c-10.9,25.6-21.2,49.8-22.2,65.9c-0.3,4.6-0.5,10-0.8,16c-2.9,57.2-9.6,191.3-52.3,191.3c-1.9,0-7.5-2.5-18.1-25.8c-7.7-16.9-15.8-39.3-24.4-63c-23.9-66.2-41-109.7-60.5-109.7c-16.3,0-30.2,37.5-51.4,98.6c-8.9,25.6-18.1,52.1-27.1,71.8c-12.3,26.9-19.7,30.9-23,30.9c-24.7,0-37.7-39.6-44.2-72.8l0,0c-1.2-6.1-2.3-12.7-3.3-19.5c-0.3-2.2-2.3-3.7-4.5-3.4c-2.2,0.3-3.7,2.4-3.4,4.5c1,7,2.2,13.7,3.4,19.9c0,0,0,0,0,0c10.3,52.6,27.8,79.2,52,79.2c18.8,0,34.3-40.8,57.7-108c8.6-24.8,16.7-48.2,24.7-65.9c11-24.7,17.1-27.3,19.2-27.3c4.4,0,12.1,5.5,24.5,31.7c9.5,20.1,19.1,46.8,28.4,72.7c21,58.4,34.7,94.2,50,94.2c50.3,0,56.8-129.4,60.3-198.9c0.3-6,0.6-11.3,0.8-15.9c0.9-14.8,10.9-38.3,21.5-63.2c12.5-29.4,25.5-59.7,25.5-80.8C467.1,98.8,458.3,72.2,442.2,53.4z"/>
  </svg>
);

// Status colors and icons
const statusConfig = {
  ok: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'OK' },
  soon: { color: 'bg-amber-100 text-amber-800', icon: Clock, label: 'Bientôt' },
  overdue: { color: 'bg-red-100 text-red-800', icon: AlertTriangle, label: 'En retard' },
  unknown: { color: 'bg-stone-100 text-stone-500', icon: null, label: '?' }
};

// Care type icons
const careIcons = {
  marechal: HorseshoeIcon,
  vaccin: Syringe,
  dentiste: ToothIcon,
  vermifuge: Pill,
  osteopathe: Activity
};

const careLabels = {
  marechal: 'Maréchal',
  vaccin: 'Vaccin',
  dentiste: 'Dentiste',
  vermifuge: 'Vermifuge',
  osteopathe: 'Ostéopathe'
};

const frequencyOptions = {
  mois: [
    { value: 0, label: 'Inconnue' },
    { value: 3, label: '3 mois' },
    { value: 6, label: '6 mois' },
    { value: 12, label: '12 mois' }
  ],
  semaines: [
    { value: 0, label: 'Inconnue' },
    { value: 4, label: '4 semaines' },
    { value: 5, label: '5 semaines' },
    { value: 6, label: '6 semaines' },
    { value: 7, label: '7 semaines' },
    { value: 8, label: '8 semaines' }
  ]
};

// Liste des produits vermifuges (classée par ordre alphabétique)
const vermifugeProducts = [
  'Equimax® (Ivermectine + Praziquantel)',
  'Eqvalan Duo® (Ivermectine + Praziquantel)',
  'Eqvalan Gold® (Ivermectine + Praziquantel)',
  'Eqvalan® (Ivermectine)',
  'Equest Pramox® (Moxidectine + Praziquantel)',
  'Equest® (Moxidectine)',
  'Eraquell Combi® (Ivermectine + Praziquantel)',
  'Eraquell® (Ivermectine)',
  'Fenbendazole Intervet® / MSD (Fenbendazole)',
  'Helminthex® (Pyrantel)',
  'Horseminth® (Pyrantel)',
  'Ivomec® cheval (Ivermectine)',
  'Noromectin® cheval (Ivermectine)',
  'Panacur® Equine (Fenbendazole)',
  'Strongid® (Pyrantel)'
];

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const StatusBadge = ({ status }) => {
  const config = statusConfig[status] || statusConfig.unknown;
  const Icon = config.icon;
  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      {Icon && <Icon className="w-3 h-3" />}
      {config.label}
    </Badge>
  );
};

export const HorseDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('table');
  const [dashboardData, setDashboardData] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedHorse, setSelectedHorse] = useState(null);
  const [editingSoins, setEditingSoins] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Quick validation state
  const [validatingCare, setValidatingCare] = useState(null); // { horse_id, horse_nom, care_type, care_label, date }
  
  // Share calendar state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharePeriod, setSharePeriod] = useState('3');
  const [shareEmail, setShareEmail] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [sharing, setSharing] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [dashRes, calRes] = await Promise.all([
        axios.get(`${API_URL}/api/horses/dashboard`),
        axios.get(`${API_URL}/api/horses/care-calendar?months_ahead=3`)
      ]);
      setDashboardData(dashRes.data);
      setCalendarData(calRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Share calendar functions
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/horses/care-calendar/pdf?months=${sharePeriod}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `calendrier_soins_${sharePeriod}mois.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF téléchargé avec succès');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement du PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePrint = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/horses/care-calendar/pdf?months=${sharePeriod}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
      
      toast.success('Ouverture de l\'impression...');
    } catch (error) {
      console.error('Error printing PDF:', error);
      toast.error('Erreur lors de l\'impression');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleSendEmail = async () => {
    if (!shareEmail) {
      toast.error('Veuillez saisir une adresse email');
      return;
    }
    
    setSharing(true);
    try {
      await axios.post(`${API_URL}/api/horses/care-calendar/send-email`, null, {
        params: {
          email: shareEmail,
          months: parseInt(sharePeriod),
          message: shareMessage
        }
      });
      
      toast.success(`Email envoyé à ${shareEmail}`);
      setShareDialogOpen(false);
      setShareEmail('');
      setShareMessage('');
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(getErrorMessage(error, 'Erreur lors de l\'envoi de l\'email'));
    } finally {
      setSharing(false);
    }
  };

  // Quick care validation
  const handleOpenValidation = (horse, careType) => {
    const today = new Date().toISOString().split('T')[0];
    setValidatingCare({
      horse_id: horse.id,
      horse_nom: horse.nom,
      care_type: careType,
      care_label: careLabels[careType],
      date: today,
      notes: horse.soins[careType].notes || '',
      nom_produit: careType === 'vermifuge' ? (horse.soins[careType].nom_produit || '') : undefined
    });
  };

  const handleValidateCare = async () => {
    if (!validatingCare) return;
    setSaving(true);
    try {
      // Map care type to database field name
      const careTypeToDbField = {
        'marechal': 'soins_marechal',
        'vaccin': 'soins_veterinaire',
        'dentiste': 'soins_dentiste',
        'vermifuge': 'soins_vermifuge',
        'osteopathe': 'soins_osteopathe'
      };
      
      const dbField = careTypeToDbField[validatingCare.care_type];
      const horse = dashboardData.horses.find(h => h.id === validatingCare.horse_id);
      
      const updateData = {
        [dbField]: {
          derniere_visite: validatingCare.date,
          notes: validatingCare.notes
        }
      };
      
      // Add specific fields based on care type
      if (validatingCare.care_type === 'marechal') {
        updateData[dbField].frequence_semaines = horse?.soins?.marechal?.frequence_semaines ?? 6;
      } else if (validatingCare.care_type === 'vermifuge') {
        updateData[dbField].frequence_mois = horse?.soins?.vermifuge?.frequence_mois ?? 6;
        updateData[dbField].produit = validatingCare.nom_produit;
      } else {
        // vaccin, dentiste, osteopathe
        const defaultFreq = validatingCare.care_type === 'osteopathe' ? 6 : 12;
        updateData[dbField].frequence_mois = horse?.soins?.[validatingCare.care_type]?.frequence_mois ?? defaultFreq;
      }
      
      await axios.put(`${API_URL}/api/horses/${validatingCare.horse_id}/soins?notify=true`, updateData);
      toast.success(`${validatingCare.care_label} validé pour ${validatingCare.horse_nom} - Propriétaires notifiés`);
      setValidatingCare(null);
      fetchDashboardData();
    } catch (error) {
      console.error('Error validating care:', error);
      toast.error('Erreur lors de la validation');
    } finally {
      setSaving(false);
    }
  };

  // Open validation modal from calendar event
  const handleOpenValidationFromCalendar = (event) => {
    const today = new Date().toISOString().split('T')[0];
    setValidatingCare({
      horse_id: event.cheval_id,
      horse_nom: event.cheval_nom,
      care_type: event.type_soin,
      care_label: event.type_soin_label,
      date: today,
      notes: '',
      nom_produit: event.type_soin === 'vermifuge' ? (event.nom_produit || '') : undefined
    });
  };

  const handleEditSoins = (horse) => {
    setEditingSoins({
      horse_id: horse.id,
      horse_nom: horse.nom,
      soin_marechal: {
        frequence_semaines: horse.soins.marechal.frequence_semaines ?? 6,
        tolerance_semaines: horse.soins.marechal.tolerance_semaines || 1,
        derniere_visite: horse.soins.marechal.derniere_visite || '',
        notes: horse.soins.marechal.notes || ''
      },
      soin_vaccin: {
        frequence_mois: horse.soins.vaccin.frequence_mois ?? 12,
        derniere_visite: horse.soins.vaccin.derniere_visite || '',
        notes: horse.soins.vaccin.notes || ''
      },
      soin_dentiste: {
        frequence_mois: horse.soins.dentiste.frequence_mois ?? 12,
        derniere_visite: horse.soins.dentiste.derniere_visite || '',
        notes: horse.soins.dentiste.notes || ''
      },
      soin_vermifuge: {
        frequence_mois: horse.soins.vermifuge.frequence_mois ?? 6,
        derniere_visite: horse.soins.vermifuge.derniere_visite || '',
        nom_produit: horse.soins.vermifuge.nom_produit || '',
        notes: horse.soins.vermifuge.notes || ''
      },
      soin_osteopathe: {
        frequence_mois: horse.soins.osteopathe.frequence_mois ?? 6,
        derniere_visite: horse.soins.osteopathe.derniere_visite || '',
        notes: horse.soins.osteopathe.notes || ''
      }
    });
  };

  const handleSaveSoins = async () => {
    if (!editingSoins) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/horses/${editingSoins.horse_id}/soins?notify=true`, {
        soins_marechal: editingSoins.soin_marechal,
        soins_veterinaire: editingSoins.soin_vaccin,
        soins_dentiste: editingSoins.soin_dentiste,
        soins_vermifuge: editingSoins.soin_vermifuge,
        soins_osteopathe: editingSoins.soin_osteopathe
      });
      toast.success('Soins mis à jour - Propriétaires notifiés');
      setEditingSoins(null);
      fetchDashboardData();
    } catch (error) {
      console.error('Error saving soins:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateEditingSoin = (soinType, field, value) => {
    setEditingSoins(prev => ({
      ...prev,
      [soinType]: {
        ...prev[soinType],
        [field]: value
      }
    }));
  };

  // Filter horses
  const filteredHorses = dashboardData?.horses?.filter(horse => {
    const matchesSearch = horse.nom.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    
    if (filterStatus === 'all') return true;
    
    // Check if any care has the selected status
    const soinsStatuses = Object.values(horse.soins).map(s => s.statut);
    if (filterStatus === 'overdue') return soinsStatuses.includes('overdue');
    if (filterStatus === 'soon') return soinsStatuses.includes('soon');
    if (filterStatus === 'ok') return soinsStatuses.every(s => s === 'ok' || s === 'unknown');
    
    return true;
  }) || [];

  // Group calendar events by month/week
  const groupedEvents = calendarData?.events?.reduce((acc, event) => {
    const date = new Date(event.date);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(event);
    return acc;
  }, {}) || {};

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="horse-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif font-bold text-[#2E4A3D]">
            Tableau de bord des chevaux
          </h1>
          <p className="text-stone-500">
            Saison {new Date(dashboardData?.saison_debut).getFullYear()}/{new Date(dashboardData?.saison_debut).getFullYear() + 1}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShareDialogOpen(true)}
            data-testid="share-btn"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Partager
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchDashboardData}
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-[#2E4A3D]">{dashboardData?.horses?.length || 0}</div>
            <div className="text-sm text-stone-500">Chevaux actifs</div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-700">
              {dashboardData?.horses?.filter(h => 
                Object.values(h.soins).some(s => s.statut === 'overdue')
              ).length || 0}
            </div>
            <div className="text-sm text-red-600">Soins en retard</div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-amber-700">
              {dashboardData?.horses?.filter(h => 
                Object.values(h.soins).some(s => s.statut === 'soon')
              ).length || 0}
            </div>
            <div className="text-sm text-amber-600">Soins à prévoir</div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-700">
              {dashboardData?.horses?.filter(h => 
                Object.values(h.soins).every(s => s.statut === 'ok' || s.statut === 'unknown')
              ).length || 0}
            </div>
            <div className="text-sm text-green-600">À jour</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="table" className="flex items-center gap-2">
            <Table2 className="w-4 h-4" />
            Tableau
          </TabsTrigger>
          <TabsTrigger value="calendar" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Calendrier des soins
          </TabsTrigger>
        </TabsList>

        {/* Table View */}
        <TabsContent value="table">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Rechercher un cheval..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-horse"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[200px]" data-testid="filter-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="overdue">En retard</SelectItem>
                <SelectItem value="soon">À prévoir</SelectItem>
                <SelectItem value="ok">À jour</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="horses-table">
                <thead className="bg-stone-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-medium text-stone-700 sticky left-0 bg-stone-50 z-10 min-w-[120px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">Cheval</th>
                    <th className="text-left p-3 font-medium text-stone-700">Propriétaire</th>
                    <th className="text-center p-3 font-medium text-stone-700">
                      <MessageSquare className="w-4 h-4 inline" title="Commentaires" />
                    </th>
                    <th className="text-center p-3 font-medium text-stone-700">
                      <HorseshoeIcon className="w-4 h-4 inline" title="Maréchal" />
                    </th>
                    <th className="text-center p-3 font-medium text-stone-700">
                      <Syringe className="w-4 h-4 inline" title="Vaccin" />
                    </th>
                    <th className="text-center p-3 font-medium text-stone-700">
                      <ToothIcon className="w-4 h-4 inline" title="Dentiste" />
                    </th>
                    <th className="text-center p-3 font-medium text-stone-700">
                      <Pill className="w-4 h-4 inline" title="Vermifuge" />
                    </th>
                    <th className="text-center p-3 font-medium text-stone-700">
                      <Activity className="w-4 h-4 inline" title="Ostéopathe" />
                    </th>
                    <th className="text-center p-3 font-medium text-stone-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHorses.map((horse) => (
                    <tr 
                      key={horse.id} 
                      className="border-b hover:bg-stone-50 transition-colors group"
                      data-testid={`horse-row-${horse.id}`}
                    >
                      <td className="p-3 sticky left-0 bg-white group-hover:bg-stone-50 z-10 min-w-[120px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] transition-colors">
                        <div className="font-medium text-stone-800">{horse.nom}</div>
                        <div className="text-xs text-stone-500">{horse.type_animal}</div>
                      </td>
                      <td className="p-3">
                        <span className={horse.proprietaire.type === 'club' ? 'text-[#2E4A3D] font-medium' : ''}>
                          {horse.proprietaire.nom}
                        </span>
                      </td>
                      <td className="p-3">
                        {/* Commentaires - Pastilles Attention/A surveiller */}
                        {(() => {
                          const comments = horse.commentaires || [];
                          const activeAttention = comments.filter(c => c.type === 'attention' && c.is_marker_active);
                          const activeSurveiller = comments.filter(c => c.type === 'a_surveiller' && c.is_marker_active);
                          
                          if (activeAttention.length === 0 && activeSurveiller.length === 0) {
                            return <span className="text-stone-400">-</span>;
                          }
                          
                          return (
                            <div className="flex items-center justify-center gap-1">
                              {activeAttention.length > 0 && (
                                <div 
                                  className="relative w-6 h-6 rounded-full bg-red-500 flex items-center justify-center cursor-pointer hover:scale-110 transition-transform"
                                  title={`${activeAttention.length} Attention ! - Cliquer pour voir`}
                                  onClick={() => navigate(`/horses?openHorse=${horse.id}&commentFilter=attention`)}
                                >
                                  <AlertTriangle className="w-3.5 h-3.5 text-white" />
                                  {activeAttention.length > 1 && (
                                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-white text-red-600 text-[10px] font-bold rounded-full flex items-center justify-center shadow border border-red-200">
                                      {activeAttention.length}
                                    </span>
                                  )}
                                </div>
                              )}
                              {activeSurveiller.length > 0 && (
                                <div 
                                  className="relative w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center cursor-pointer hover:scale-110 transition-transform"
                                  title={`${activeSurveiller.length} À surveiller - Cliquer pour voir`}
                                  onClick={() => navigate(`/horses?openHorse=${horse.id}&commentFilter=a_surveiller`)}
                                >
                                  <Eye className="w-3.5 h-3.5 text-white" />
                                  {activeSurveiller.length > 1 && (
                                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-white text-orange-600 text-[10px] font-bold rounded-full flex items-center justify-center shadow border border-orange-200">
                                      {activeSurveiller.length}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      {/* Care columns - clickable to validate */}
                      {['marechal', 'vaccin', 'dentiste', 'vermifuge', 'osteopathe'].map((care) => {
                        const soin = horse.soins[care];
                        const isUnknownFreq = soin.statut === 'unknown';
                        return (
                          <td key={care} className="p-2 text-center">
                            <button
                              onClick={() => handleOpenValidation(horse, care)}
                              className="flex flex-col items-center w-full hover:bg-stone-100 rounded p-1 transition-colors cursor-pointer"
                              title={`Cliquer pour valider ${careLabels[care]}`}
                              data-testid={`validate-${care}-${horse.id}`}
                            >
                              {/* Dernière date */}
                              <span className="text-xs font-medium text-stone-700">
                                {soin.derniere_visite ? formatDate(soin.derniere_visite) : '-'}
                              </span>
                              {/* Prévision avec badge de statut */}
                              <div className="mt-1">
                                {isUnknownFreq ? (
                                  <span className="text-xs text-stone-400">?</span>
                                ) : (
                                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${statusConfig[soin.statut]?.color || 'bg-stone-100 text-stone-500'}`}>
                                    {statusConfig[soin.statut]?.icon && (
                                      <span className="w-3 h-3">
                                        {React.createElement(statusConfig[soin.statut].icon, { className: 'w-3 h-3' })}
                                      </span>
                                    )}
                                    {formatDate(soin.prochaine_date)}
                                  </span>
                                )}
                              </div>
                            </button>
                          </td>
                        );
                      })}
                      <td className="p-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditSoins(horse)}
                          title="Modifier les fréquences"
                          data-testid={`edit-soins-${horse.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredHorses.length === 0 && (
                <div className="text-center py-8 text-stone-500">
                  Aucun cheval trouvé
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Calendar View */}
        <TabsContent value="calendar">
          <div className="space-y-6">
            {Object.entries(groupedEvents).map(([monthKey, events]) => {
              const [year, month] = monthKey.split('-');
              const monthName = new Date(year, parseInt(month) - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
              
              // Group events by care type within the month
              const eventsByCareType = events.reduce((acc, event) => {
                if (!acc[event.type_soin]) acc[event.type_soin] = [];
                acc[event.type_soin].push(event);
                return acc;
              }, {});
              
              // Order of care types for display
              const careTypeOrder = ['marechal', 'vaccin', 'dentiste', 'vermifuge', 'osteopathe'];
              const sortedCareTypes = careTypeOrder.filter(ct => eventsByCareType[ct]);
              
              return (
                <Card key={monthKey}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg capitalize">{monthName}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {sortedCareTypes.map(careType => {
                        const careEvents = eventsByCareType[careType];
                        const Icon = careIcons[careType];
                        const careLabel = careLabels[careType];
                        
                        return (
                          <div key={careType} className="space-y-2">
                            {/* Care type header */}
                            <div className="flex items-center gap-2 text-sm font-semibold text-stone-700 border-b border-stone-200 pb-1">
                              {Icon && <Icon className="w-4 h-4" />}
                              <span>{careLabel}</span>
                              <span className="text-stone-400 font-normal">({careEvents.length})</span>
                            </div>
                            
                            {/* Events for this care type */}
                            <div className="space-y-2 pl-6">
                              {careEvents.map((event, idx) => (
                                <button 
                                  key={idx}
                                  onClick={() => handleOpenValidationFromCalendar(event)}
                                  className={`w-full flex items-center justify-between p-2 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                                    event.statut === 'overdue' ? 'bg-red-50 border-red-200 hover:bg-red-100' :
                                    event.statut === 'soon' ? 'bg-amber-50 border-amber-200 hover:bg-amber-100' :
                                    'bg-stone-50 border-stone-200 hover:bg-stone-100'
                                  }`}
                                  data-testid={`calendar-event-${event.cheval_id}-${event.type_soin}`}
                                  title="Cliquer pour valider ce soin"
                                >
                                  <div className="font-medium text-sm text-left">{event.cheval_nom}</div>
                                  <div className="flex items-center gap-2">
                                    <div className="text-sm text-right">
                                      <div className="font-medium">{formatDate(event.date)}</div>
                                      {event.nom_produit && (
                                        <div className="text-xs text-stone-500">{event.nom_produit}</div>
                                      )}
                                    </div>
                                    <StatusBadge status={event.statut} />
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            
            {Object.keys(groupedEvents).length === 0 && (
              <Card>
                <CardContent className="py-8 text-center text-stone-500">
                  Aucun soin prévu dans les 3 prochains mois
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Soins Modal */}
      {editingSoins && (
        <Dialog open={!!editingSoins} onOpenChange={() => setEditingSoins(null)}>
          <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle className="font-serif text-lg sm:text-xl">
                Soins de {editingSoins.horse_nom}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 sm:space-y-6 mt-4">
              {/* Maréchal */}
              <div className="border rounded-lg p-3 sm:p-4 space-y-3">
                <div className="flex items-center gap-2 text-stone-700 font-medium">
                  <HorseshoeIcon className="w-5 h-5" />
                  Maréchal-ferrant
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Fréquence</Label>
                    <Select 
                      value={String(editingSoins.soin_marechal.frequence_semaines)}
                      onValueChange={(v) => updateEditingSoin('soin_marechal', 'frequence_semaines', parseInt(v))}
                    >
                      <SelectTrigger data-testid="marechal-frequence">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {frequencyOptions.semaines.map(opt => (
                          <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Dernier ferrage</Label>
                    <Input
                      type="date"
                      value={editingSoins.soin_marechal.derniere_visite?.split('T')[0] || ''}
                      onChange={(e) => updateEditingSoin('soin_marechal', 'derniere_visite', e.target.value)}
                      data-testid="marechal-date"
                    />
                  </div>
                </div>
              </div>

              {/* Vaccin */}
              <div className="border rounded-lg p-3 sm:p-4 space-y-3">
                <div className="flex items-center gap-2 text-stone-700 font-medium">
                  <Syringe className="w-5 h-5" />
                  Vaccin
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Fréquence</Label>
                    <Select 
                      value={String(editingSoins.soin_vaccin.frequence_mois)}
                      onValueChange={(v) => updateEditingSoin('soin_vaccin', 'frequence_mois', parseInt(v))}
                    >
                      <SelectTrigger data-testid="vaccin-frequence">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {frequencyOptions.mois.map(opt => (
                          <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Dernier vaccin</Label>
                    <Input
                      type="date"
                      value={editingSoins.soin_vaccin.derniere_visite?.split('T')[0] || ''}
                      onChange={(e) => updateEditingSoin('soin_vaccin', 'derniere_visite', e.target.value)}
                      data-testid="vaccin-date"
                    />
                  </div>
                </div>
              </div>

              {/* Dentiste */}
              <div className="border rounded-lg p-3 sm:p-4 space-y-3">
                <div className="flex items-center gap-2 text-stone-700 font-medium">
                  <ToothIcon className="w-5 h-5" />
                  Dentiste
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Fréquence</Label>
                    <Select 
                      value={String(editingSoins.soin_dentiste.frequence_mois)}
                      onValueChange={(v) => updateEditingSoin('soin_dentiste', 'frequence_mois', parseInt(v))}
                    >
                      <SelectTrigger data-testid="dentiste-frequence">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {frequencyOptions.mois.map(opt => (
                          <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Dernière consultation</Label>
                    <Input
                      type="date"
                      value={editingSoins.soin_dentiste.derniere_visite?.split('T')[0] || ''}
                      onChange={(e) => updateEditingSoin('soin_dentiste', 'derniere_visite', e.target.value)}
                      data-testid="dentiste-date"
                    />
                  </div>
                </div>
              </div>

              {/* Vermifuge */}
              <div className="border rounded-lg p-3 sm:p-4 space-y-3">
                <div className="flex items-center gap-2 text-stone-700 font-medium">
                  <Pill className="w-5 h-5" />
                  Vermifuge
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm">Fréquence</Label>
                    <Select 
                      value={String(editingSoins.soin_vermifuge.frequence_mois)}
                      onValueChange={(v) => updateEditingSoin('soin_vermifuge', 'frequence_mois', parseInt(v))}
                    >
                      <SelectTrigger data-testid="vermifuge-frequence">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {frequencyOptions.mois.map(opt => (
                          <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Dernier vermifuge</Label>
                    <Input
                      type="date"
                      value={editingSoins.soin_vermifuge.derniere_visite?.split('T')[0] || ''}
                      onChange={(e) => updateEditingSoin('soin_vermifuge', 'derniere_visite', e.target.value)}
                      data-testid="vermifuge-date"
                    />
                  </div>
                  <div className="sm:col-span-2 lg:col-span-1">
                    <Label className="text-sm">Nom du produit</Label>
                    <Select
                      value={editingSoins.soin_vermifuge.nom_produit || ''}
                      onValueChange={(v) => updateEditingSoin('soin_vermifuge', 'nom_produit', v === 'none' ? '' : v)}
                    >
                      <SelectTrigger data-testid="vermifuge-produit">
                        <SelectValue placeholder="Sélectionner un produit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Aucun --</SelectItem>
                        {vermifugeProducts.map(product => (
                          <SelectItem key={product} value={product}>{product}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Ostéopathe */}
              <div className="border rounded-lg p-3 sm:p-4 space-y-3">
                <div className="flex items-center gap-2 text-stone-700 font-medium">
                  <Stethoscope className="w-5 h-5" />
                  Ostéopathe
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Fréquence</Label>
                    <Select 
                      value={String(editingSoins.soin_osteopathe.frequence_mois)}
                      onValueChange={(v) => updateEditingSoin('soin_osteopathe', 'frequence_mois', parseInt(v))}
                    >
                      <SelectTrigger data-testid="osteo-frequence">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {frequencyOptions.mois.map(opt => (
                          <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Dernière séance</Label>
                    <Input
                      type="date"
                      value={editingSoins.soin_osteopathe.derniere_visite?.split('T')[0] || ''}
                      onChange={(e) => updateEditingSoin('soin_osteopathe', 'derniere_visite', e.target.value)}
                      data-testid="osteo-date"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setEditingSoins(null)}
                >
                  <X className="w-4 h-4 mr-2" />
                  Annuler
                </Button>
                <Button
                  onClick={handleSaveSoins}
                  disabled={saving}
                  className="bg-[#2E4A3D] hover:bg-[#1a2d24] text-white"
                  data-testid="save-soins-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Quick Care Validation Modal */}
      {validatingCare && (
        <Dialog open={!!validatingCare} onOpenChange={() => setValidatingCare(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif flex items-center gap-2">
                {React.createElement(careIcons[validatingCare.care_type], { className: "w-5 h-5" })}
                Valider {validatingCare.care_label}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div className="text-center p-4 bg-stone-50 rounded-lg">
                <p className="text-lg font-medium text-[#2E4A3D]">{validatingCare.horse_nom}</p>
                <p className="text-sm text-stone-500">{validatingCare.care_label} effectué</p>
              </div>

              <div>
                <Label className="text-sm">Date du soin</Label>
                <Input
                  type="date"
                  value={validatingCare.date}
                  onChange={(e) => setValidatingCare({...validatingCare, date: e.target.value})}
                  className="mt-1"
                  data-testid="validation-date"
                />
              </div>

              {validatingCare.care_type === 'vermifuge' && (
                <div>
                  <Label className="text-sm">Nom du produit</Label>
                  <Select
                    value={validatingCare.nom_produit || ''}
                    onValueChange={(v) => setValidatingCare({...validatingCare, nom_produit: v === 'none' ? '' : v})}
                  >
                    <SelectTrigger className="mt-1" data-testid="validation-produit">
                      <SelectValue placeholder="Sélectionner un produit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Aucun --</SelectItem>
                      {vermifugeProducts.map(product => (
                        <SelectItem key={product} value={product}>{product}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="text-sm">Notes (optionnel)</Label>
                <Input
                  value={validatingCare.notes || ''}
                  onChange={(e) => setValidatingCare({...validatingCare, notes: e.target.value})}
                  placeholder="Observations, remarques..."
                  className="mt-1"
                  data-testid="validation-notes"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setValidatingCare(null)}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleValidateCare}
                  disabled={saving || !validatingCare.date}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="confirm-validation-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {saving ? 'Validation...' : 'Valider le soin'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Share Calendar Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#2E4A3D]" />
              Partager le calendrier des soins
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Period selection */}
            <div>
              <Label className="text-sm font-medium">Période</Label>
              <Select value={sharePeriod} onValueChange={setSharePeriod}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 mois</SelectItem>
                  <SelectItem value="3">3 mois</SelectItem>
                  <SelectItem value="6">6 mois</SelectItem>
                  <SelectItem value="12">12 mois (1 an)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                className="flex items-center justify-center gap-2"
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileDown className="w-4 h-4" />
                )}
                Télécharger PDF
              </Button>
              <Button
                variant="outline"
                onClick={handlePrint}
                disabled={downloadingPdf}
                className="flex items-center justify-center gap-2"
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Printer className="w-4 h-4" />
                )}
                Imprimer
              </Button>
            </div>

            {/* Separator */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-stone-500">Ou envoyer par email</span>
              </div>
            </div>

            {/* Email form */}
            <div>
              <Label className="text-sm font-medium">Adresse email du destinataire</Label>
              <Input
                type="email"
                placeholder="exemple@email.com"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Message (optionnel)</Label>
              <Textarea
                placeholder="Ajouter un message personnalisé..."
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
                className="mt-1"
                rows={3}
              />
            </div>

            <Button
              onClick={handleSendEmail}
              disabled={sharing || !shareEmail}
              className="w-full bg-[#2E4A3D] hover:bg-[#1e3228]"
            >
              {sharing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Envoyer par email
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
